import { useCallback, useEffect, type KeyboardEvent } from 'react';
import { useDispatch } from 'react-redux';
import { ArrowRightIcon, CopyIcon } from '@rsos/assets/icons';
import { Text } from '@rsos/base-components';
import { Loader } from '@rsos/base-components/Button/Loader';
import { ButtonText } from '@rsos/components/capstone/Location/styles/common.styles';
import { Button, Accordion } from '@rsos/components/capstone/base';
import { setSelectedCallerID } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import type { StatusDisplayItem } from '@rsos/reduxDataStore/src/features/resourceRequests';
import { useTheme } from '@rsos/theme/styled';
import copyToClipboard from '@rsos/utils/copyToClipboard';
import { formatInternationalPhoneNumberHuman } from '@rsos/utils/internationalPhoneNumberUtils';
import {
  trackStatusPageRender,
  trackOpenTransportLink,
  trackCopyTrackingLink,
} from '../../../gainsightTrackings/airAmbulanceTrackings';
import { FullWidthContent } from '../../JurisdictionView/EventPane/tabs/OverviewTab/OverviewTab.styles';
import { STATUS_PAGE_STATES } from '../AirAmbulanceForm/constants';
import {
  BannerWrapper,
  IconWrapper,
  BannerText,
} from '../Banner/StatusBanner.styles';
import styles from './AirAmbulanceEtlStatus.module.css';
import {
  FlexRow,
  FlexRowSpaceBetween,
  MarginWrapper,
  VerticalSpacing,
  FullWidthDiv,
} from './AirAmbulanceEtlStatus.styles';
import AirAmbulanceEtlStatusControls from './AirAmbulanceEtlStatusControls';
import { getActiveDispatchStatusDisplay } from './etlStatusUtils';
import { useAirAmbulanceEtlStatus } from './hooks';

/**
 * Main status display component for ETL-based Air Ambulance requests.
 */
function AirAmbulanceEtlStatus(): JSX.Element {
  const {
    transport,
    selectedRequestId,
    isLoading,
    statusPageState,
    latestDisplayableStatus,
    remainingStatuses,
    brandTail,
    trackingUrl,
    formattedStatuses,
    pickupAddress,
    destination,
    latestDispatchCenter,
  } = useAirAmbulanceEtlStatus();

  const hasTrackingData = brandTail || trackingUrl;
  const hasAnyStatuses = formattedStatuses.length > 0;
  const shouldShowTracking =
    statusPageState !== STATUS_PAGE_STATES.SUBMITTED ||
    hasTrackingData ||
    hasAnyStatuses;

  const bannerStatusDisplay = transport
    ? getActiveDispatchStatusDisplay(transport)
    : undefined;

  return (
    <div>
      <MarginWrapper>
        <EtlStatusBanner
          statusPageState={statusPageState}
          latestStatusDisplay={bannerStatusDisplay}
        />
      </MarginWrapper>

      {shouldShowTracking && (
        <EtlTrackingInfo
          brandTail={brandTail}
          trackingUrl={trackingUrl}
          requestId={selectedRequestId}
        />
      )}

      <EtlTransportDetails
        pickupAddress={pickupAddress}
        destination={destination}
        dispatchCenterName={latestDispatchCenter?.name}
        dispatchCenterPhone={latestDispatchCenter?.phoneNumber}
      />

      {transport?.caller_id && (
        <EtlRelatedCall callerId={transport.caller_id} />
      )}

      <EtlStatusUpdates
        latestDisplayableStatus={latestDisplayableStatus}
        remainingStatuses={remainingStatuses}
        requestId={selectedRequestId}
        isLoading={isLoading}
      />

      {transport && <AirAmbulanceEtlStatusControls transport={transport} />}
    </div>
  );
}

function formatTime(timestamp: string | undefined): string {
  if (!timestamp) return 'N/A';
  try {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } catch {
    return 'N/A';
  }
}

interface EtlStatusBannerProps {
  statusPageState: (typeof STATUS_PAGE_STATES)[keyof typeof STATUS_PAGE_STATES];
  latestStatusDisplay?: string;
}

function EtlStatusBanner({
  statusPageState,
  latestStatusDisplay,
}: EtlStatusBannerProps): JSX.Element {
  const IconComponent = statusPageState.icon;
  const displayText = latestStatusDisplay
    ? `Air ambulance status: ${latestStatusDisplay}`
    : statusPageState.message;

  return (
    <BannerWrapper bgColor={statusPageState.bgColor} horizontal>
      <IconWrapper>
        <IconComponent color="background" height={16} width={16} />
      </IconWrapper>
      <BannerText>{displayText}</BannerText>
    </BannerWrapper>
  );
}

interface EtlTransportDetailsProps {
  pickupAddress: string | null;
  destination: string | null;
  dispatchCenterName: string | null | undefined;
  dispatchCenterPhone: string | null | undefined;
}

function EtlTransportDetails({
  pickupAddress,
  destination,
  dispatchCenterName,
  dispatchCenterPhone,
}: EtlTransportDetailsProps): JSX.Element {
  return (
    <MarginWrapper style={{ marginTop: '16px', marginBottom: '16px' }}>
      <FullWidthContent>
        <div className={styles.detailRows}>
          <div>
            <Text color="primaryText" fontWeight="bold" size="normal">
              Pickup
            </Text>
            <Text color="secondaryText" size="normal">
              {pickupAddress || 'TBD'}
            </Text>
          </div>
          <div>
            <Text color="primaryText" fontWeight="bold" size="normal">
              Destination
            </Text>
            <Text color="secondaryText" size="normal">
              {destination || 'TBD'}
            </Text>
          </div>
          {dispatchCenterName && (
            <div>
              <Text color="primaryText" fontWeight="bold" size="normal">
                Air Dispatch
              </Text>
              <Text color="secondaryText" size="normal">
                {dispatchCenterName}
              </Text>
            </div>
          )}
          {dispatchCenterPhone && (
            <div>
              <Text color="primaryText" fontWeight="bold" size="normal">
                Air Dispatch Phone
              </Text>
              <Text color="secondaryText" size="normal">
                {dispatchCenterPhone}
              </Text>
            </div>
          )}
        </div>
      </FullWidthContent>
    </MarginWrapper>
  );
}

interface EtlRelatedCallProps {
  callerId: string;
}

function EtlRelatedCall({ callerId }: EtlRelatedCallProps): JSX.Element {
  const dispatch = useDispatch();

  const handleViewRelatedCall = useCallback(() => {
    dispatch(setSelectedCallerID(callerId));
  }, [dispatch, callerId]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleViewRelatedCall();
      }
    },
    [handleViewRelatedCall],
  );

  return (
    <MarginWrapper style={{ marginTop: '16px', marginBottom: '16px' }}>
      <FullWidthContent
        className={styles.relatedCallCard}
        role="button"
        tabIndex={0}
        aria-label="View related call"
        onClick={handleViewRelatedCall}
        onKeyDown={handleKeyDown}
      >
        <div className={styles.relatedCallHeader}>
          <Text color="primaryText" fontWeight="bold" size="normal">
            Related Call
          </Text>
          <ArrowRightIcon width={16} height={16} color="primaryText" />
        </div>
        <Text color="secondaryText" size="normal">
          {formatInternationalPhoneNumberHuman(callerId)}
        </Text>
      </FullWidthContent>
    </MarginWrapper>
  );
}

interface EtlTrackingInfoProps {
  /** @see AirAmbulanceEtlStatusResult.brandTail */
  brandTail: string | null;
  /** @see AirAmbulanceEtlStatusResult.trackingUrl */
  trackingUrl: string | null;
  requestId: string | null;
}

function EtlTrackingInfo({
  brandTail,
  trackingUrl,
  requestId,
}: EtlTrackingInfoProps): JSX.Element | null {
  const handleCopyLink = () => {
    if (trackingUrl) {
      copyToClipboard(trackingUrl);
    }
    trackCopyTrackingLink({
      airAmbulanceUrl: trackingUrl || undefined,
      requestId: requestId || undefined,
    });
  };

  const handleTrackAirAmbulance = () => {
    if (trackingUrl) {
      window.open(trackingUrl, '_blank', 'noopener,noreferrer');
    }
    trackOpenTransportLink({
      airAmbulanceUrl: trackingUrl || undefined,
      requestId: requestId || undefined,
    });
  };

  if (!brandTail && !trackingUrl) {
    return null;
  }

  return (
    <>
      <MarginWrapper>
        {brandTail && (
          <VerticalSpacing>
            <Text color="secondaryText" size="normal">
              Aircraft Name - Tail Number
            </Text>
            <Text color="primaryText" size="normal">
              {brandTail}
            </Text>
          </VerticalSpacing>
        )}
      </MarginWrapper>
      {trackingUrl && (
        <div className={styles.trackingActions}>
          <Button
            type="button"
            grow
            noMargin
            btnType="tertiary"
            onClick={handleTrackAirAmbulance}
          >
            <ButtonText>Track Air Ambulance</ButtonText>
          </Button>
          <Button
            type="button"
            btnType="tertiary"
            noMargin
            title="Copy tracking link"
            onClick={handleCopyLink}
            style={{ aspectRatio: '1 / 1', padding: 0, minWidth: 'unset' }}
          >
            <CopyIcon />
          </Button>
        </div>
      )}
    </>
  );
}

interface StatusItemProps {
  status: StatusDisplayItem;
  index: number;
}

function StatusItem({ status, index }: StatusItemProps): JSX.Element {
  return (
    <FlexRow key={`${status.timestamp || index}-${index}`}>
      <Text color="primaryBase" size="normal">
        {formatTime(status.timestamp)}
      </Text>
      <Text ml={2} color="secondaryText" size="normal">
        {status.display}
      </Text>
    </FlexRow>
  );
}

interface StatusUpdateHeaderProps {
  latestDisplayableStatus: StatusDisplayItem | null;
  isLoading: boolean;
}

function StatusUpdateHeader({
  latestDisplayableStatus,
  isLoading,
}: StatusUpdateHeaderProps): JSX.Element {
  const theme = useTheme();

  return (
    <div>
      <Text mb={2} color="primaryText" fontWeight="bold" size="normal">
        Status Updates
      </Text>
      <FlexRow>
        {latestDisplayableStatus?.display ? (
          <>
            <Text color="primaryBase" size="normal">
              {formatTime(latestDisplayableStatus.timestamp)}
            </Text>
            <Text color="secondaryText" size="normal">
              {latestDisplayableStatus.display}
            </Text>
          </>
        ) : (
          <>
            <Text color="secondaryText" size="normal">
              Awaiting status updates...
            </Text>
            {isLoading && (
              <Loader size={16} color={theme.colors.secondaryText} />
            )}
          </>
        )}
      </FlexRow>
    </div>
  );
}

interface RemainingStatusesListProps {
  statuses: StatusDisplayItem[];
}

function RemainingStatusesList({
  statuses,
}: RemainingStatusesListProps): JSX.Element {
  return (
    <>
      {statuses
        .filter(status => status.display && status.timestamp)
        .map((status, index) => (
          <StatusItem
            key={`${status.timestamp || index}-${index}`}
            status={status}
            index={index}
          />
        ))}
    </>
  );
}

interface EtlStatusUpdatesProps {
  latestDisplayableStatus: StatusDisplayItem | null;
  remainingStatuses: StatusDisplayItem[];
  requestId: string | null;
  isLoading: boolean;
}

function EtlStatusUpdates({
  latestDisplayableStatus,
  remainingStatuses,
  requestId,
  isLoading,
}: EtlStatusUpdatesProps): JSX.Element {
  useEffect(() => {
    const statusDisplay = latestDisplayableStatus?.display;
    if (statusDisplay && requestId) {
      trackStatusPageRender({
        requestId: requestId,
        status: statusDisplay,
      });
    }
  }, [requestId, latestDisplayableStatus?.display]);

  return (
    <MarginWrapper style={{ marginTop: '16px', marginBottom: '16px' }}>
      <FullWidthContent>
        <div>
          <FlexRowSpaceBetween style={{ marginTop: 0, marginBottom: 0 }}>
            <FullWidthDiv>
              {remainingStatuses.length > 0 ? (
                <Accordion
                  header={
                    <StatusUpdateHeader
                      latestDisplayableStatus={latestDisplayableStatus}
                      isLoading={isLoading}
                    />
                  }
                  headerIcon="chevron"
                  interactionType="button"
                  noBorder
                  width="100%"
                  headerPadding="0"
                  padding="0"
                  content={
                    <RemainingStatusesList statuses={remainingStatuses} />
                  }
                />
              ) : (
                <StatusUpdateHeader
                  latestDisplayableStatus={latestDisplayableStatus}
                  isLoading={isLoading}
                />
              )}
            </FullWidthDiv>
          </FlexRowSpaceBetween>
        </div>
      </FullWidthContent>
    </MarginWrapper>
  );
}

export default AirAmbulanceEtlStatus;
